import React, { useState, useEffect, useRef, MouseEvent } from "react";
import { useNavigate } from "react-router-dom";
import {
  PlusIcon,
  ArrowLeftIcon,
  MagnifyingGlassIcon,
  TrashIcon,
  CheckIcon,
} from "@heroicons/react/24/solid";
import NewProjectDialog from "./NewProjectDialog";
import api from "../services/api";
import { useAuth } from "../context/AuthContext";

interface Project {
  id: string;
  name: string;
  thumbnail?: string;
  product_image_url?: string;
  product_prompt?: string;
  createdAt: Date;
  selected?: boolean;
}

interface SelectionRect {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
}

export default function ProductVideos({ itemType }: { itemType: string }) {
  const [projects, setProjects] = useState<Project[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedProjects, setSelectedProjects] = useState<string[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [selectionRect, setSelectionRect] = useState<SelectionRect | null>(
    null
  );
  const [contextMenuPosition, setContextMenuPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [contextMenuProjectId, setContextMenuProjectId] = useState<
    string | null
  >(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const projectRefs = useRef<Map<string, DOMRect>>(new Map());
  const navigate = useNavigate();
  const { user } = useAuth();

  // Fetch projects when component mounts
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setIsLoading(true);
        const response = await api.get(`/api/projects?item_type=${itemType}`);

        // Transform the API response to match our Project interface
        const projectsData = response.data.projects.map((project: any) => ({
          id: project.id,
          name: project.name,
          product_image_url: project.product_image_url,
          // Use product_image_url as the thumbnail if available
          thumbnail: project.product_image_url || undefined,
          createdAt: new Date(project.created_at),
        }));

        console.log("Projects loaded with thumbnails:", projectsData);
        setProjects(projectsData);
        setError(null);
      } catch (err) {
        console.error("Error fetching projects:", err);
        setError("Failed to load projects. Please try again later.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchProjects();
  }, []);

  useEffect(() => {
    const planId = localStorage.getItem("selected_plan_id");
    const isAnnual = localStorage.getItem("selected_plan_annual");

    console.log("Plan ID:", planId);
    console.log("Is Annual:", isAnnual);

    console.log("User:", user);
    if (user?.is_premium !== "active" && user?.is_premium !== "trialing") {
      if (planId && isAnnual) {
        const createCheckoutSession = async () => {
          try {
            // Call the backend to create a checkout session using api service
            const response = await api.post(
              "/api/payment/create-checkout-session",
              {
                plan_id: planId,
                is_annual: isAnnual || false,
              }
            );

            const { url } = response.data;

            // Redirect to checkout page with the client secret
            // navigate(`/checkout?client_secret=${client_secret}`);
            localStorage.removeItem("selected_plan_id");
            localStorage.removeItem("selected_plan_annual");
            window.location.href = url;
          } catch (error) {
            navigate("/pricing");
            console.error("Error creating checkout session:", error);
          }
        };
        createCheckoutSession();
      } else {
        navigate("/pricing");
      }
    }
  }, []);

  const filteredProjects = projects.filter((project) =>
    project.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Calculate project element positions and store them
  useEffect(() => {
    projectRefs.current.clear();
    const projectElements = document.querySelectorAll("[data-project-id]");

    projectElements.forEach((element) => {
      const projectId = element.getAttribute("data-project-id");
      if (projectId) {
        projectRefs.current.set(projectId, element.getBoundingClientRect());
      }
    });
  }, [filteredProjects]);

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setContextMenuPosition(null);
      setContextMenuProjectId(null);
    };

    document.addEventListener("click", handleClickOutside);

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  const handleCreateNewProject = () => {
    if (itemType === "product") {
      setIsDialogOpen(true);
    } else {
      handleProjectCreated("Untitled Project", []);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleProjectCreated = async (name: string, images: File[]) => {
    try {
      // Create the project first
      const response = await api.post("/api/projects/new-project", {
        name: name || `Untitled ${itemType} Project`,
        item_type: itemType,
      });

      const projectData = response.data;
      console.log("Project created:", projectData);

      // Create a new project for the UI
      const newProject: Project = {
        id: projectData.id,
        name: projectData.name,
        product_image_url: projectData.product_image_url,
        // Use product_image_url first, then fall back to uploaded image preview if available
        thumbnail:
          projectData.product_image_url ||
          (images.length > 0 ? URL.createObjectURL(images[0]) : undefined),
        createdAt: new Date(projectData.created_at),
      };

      setProjects([...projects, newProject]);

      // Navigate to the project editor while uploads happen in the background
      navigate(`/generate-${itemType}-video`, {
        state: {
          projectId: newProject.id,
          projectName: newProject.name,
          imageCount: images.length,
          isUploading: true,
          productPrompt: newProject.product_prompt,
          images: images, // Pass the images to the next page
        },
      });
    } catch (error) {
      console.error("Error creating project:", error);
    }
  };

  const handleProjectClick = (project: Project, e: React.MouseEvent) => {
    // If we're holding Ctrl/Cmd key, toggle selection of this project
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      if (selectedProjects.includes(project.id)) {
        setSelectedProjects(selectedProjects.filter((id) => id !== project.id));
      } else {
        setSelectedProjects([...selectedProjects, project.id]);
      }
    } else if (!selectedProjects.includes(project.id)) {
      // Clear selection if this project isn't selected
      setSelectedProjects([]);
      // Navigate to project
      navigate(`/generate-${itemType}-video`, {
        state: {
          projectId: project.id,
          projectName: project.name,
          productPrompt: project.product_prompt,
        },
      });
    } else {
      // If project is already selected, navigate to it
      navigate(`/generate-${itemType}-video`, {
        state: {
          projectId: project.id,
          projectName: project.name,
          productPrompt: project.product_prompt,
        },
      });
    }
  };

  const handleMouseDown = (e: MouseEvent<HTMLDivElement>) => {
    // Start selection on any part of the container, not just when clicking directly on e.currentTarget
    if (e.button === 0 && containerRef.current) {
      // Don't start selection if we're clicking directly on a project card or the new project button
      if (
        (e.target as HTMLElement).closest("[data-project-id]") ||
        (e.target as HTMLElement).closest(".new-project-card")
      ) {
        return;
      }

      const containerRect = containerRef.current.getBoundingClientRect();
      const startX = e.clientX - containerRect.left;
      const startY = e.clientY - containerRect.top;

      setIsDragging(true);
      setSelectionRect({
        startX,
        startY,
        endX: startX,
        endY: startY,
      });

      // Clear selection if not holding Ctrl/Cmd
      if (!(e.ctrlKey || e.metaKey)) {
        setSelectedProjects([]);
      }

      // Prevent default behavior to avoid text selection
      e.preventDefault();
    }
  };

  const handleMouseMove = (e: MouseEvent<HTMLDivElement>) => {
    if (isDragging && selectionRect && containerRef.current) {
      const containerRect = containerRef.current.getBoundingClientRect();
      const endX = e.clientX - containerRect.left;
      const endY = e.clientY - containerRect.top;

      setSelectionRect({
        ...selectionRect,
        endX,
        endY,
      });

      // Check which projects are in the selection rectangle
      const selectedIds = getProjectsInSelectionRect();
      setSelectedProjects((prev) => {
        // Keep previously selected projects if Ctrl/Cmd is held
        const baseSelection = e.ctrlKey || e.metaKey ? prev : [];
        // Add newly selected projects, avoiding duplicates
        return Array.from(new Set([...baseSelection, ...selectedIds]));
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setSelectionRect(null);
  };

  const getProjectsInSelectionRect = () => {
    if (!selectionRect || !containerRef.current) return [];

    // Calculate the normalized selection rectangle (ensuring startX < endX, startY < endY)
    const selRect = {
      left: Math.min(selectionRect.startX, selectionRect.endX),
      right: Math.max(selectionRect.startX, selectionRect.endX),
      top: Math.min(selectionRect.startY, selectionRect.endY),
      bottom: Math.max(selectionRect.startY, selectionRect.endY),
    };

    const containerRect = containerRef.current.getBoundingClientRect();

    // Find projects that intersect with the selection rectangle
    const selectedIds: string[] = [];

    projectRefs.current.forEach((rect, projectId) => {
      // Adjust rect to be relative to the container
      const relRect = {
        left: rect.left - containerRect.left,
        right: rect.right - containerRect.left,
        top: rect.top - containerRect.top,
        bottom: rect.bottom - containerRect.top,
      };

      // Check for intersection
      if (
        relRect.right >= selRect.left &&
        relRect.left <= selRect.right &&
        relRect.bottom >= selRect.top &&
        relRect.top <= selRect.bottom
      ) {
        selectedIds.push(projectId);
      }
    });

    return selectedIds;
  };

  const handleContextMenu = (e: React.MouseEvent, projectId: string) => {
    e.preventDefault();
    setContextMenuPosition({ x: e.clientX, y: e.clientY });
    setContextMenuProjectId(projectId);

    // If the project is not already selected, make it the only selection
    if (!selectedProjects.includes(projectId)) {
      setSelectedProjects([projectId]);
    }
  };

  const handleDeleteProjects = async (projectIds: string[]) => {
    if (projectIds.length === 0) return;

    const confirmMessage =
      projectIds.length === 1
        ? "Are you sure you want to delete this project?"
        : `Are you sure you want to delete these ${projectIds.length} projects?`;

    if (window.confirm(confirmMessage)) {
      setIsDeleting(true);

      try {
        // Delete each project one by one
        const failedProjects: string[] = [];

        for (const id of projectIds) {
          try {
            await api.delete(`/api/projects/${id}`);
          } catch (error) {
            console.error(`Error deleting project ${id}:`, error);
            failedProjects.push(id);
          }
        }

        // Check if any projects failed to delete
        if (failedProjects.length > 0) {
          console.error("Failed to delete some projects:", failedProjects);

          // Alert the user but only show up to 3 IDs to keep the message reasonable
          const failedIdsDisplay =
            failedProjects.length <= 3
              ? failedProjects.join(", ")
              : `${failedProjects.slice(0, 3).join(", ")}... and ${
                  failedProjects.length - 3
                } more`;

          alert(
            `Failed to delete ${failedProjects.length} project(s): ${failedIdsDisplay}. The rest were deleted successfully.`
          );
        }

        // Update the projects list after deletion - remove all successfully deleted projects
        const successfullyDeletedIds = projectIds.filter(
          (id) => !failedProjects.includes(id)
        );
        setProjects((prevProjects) =>
          prevProjects.filter(
            (project) => !successfullyDeletedIds.includes(project.id)
          )
        );

        // Clear selection
        setSelectedProjects([]);
      } catch (error) {
        console.error("Error in delete operation:", error);
        alert(
          "An unexpected error occurred during deletion. Some projects may have been deleted."
        );
      } finally {
        setIsDeleting(false);
        setContextMenuPosition(null);
        setContextMenuProjectId(null);
      }
    }
  };

  // Calculate selection rectangle style
  const selectionRectStyle = selectionRect
    ? {
        position: "absolute" as const,
        left: `${Math.min(selectionRect.startX, selectionRect.endX)}px`,
        top: `${Math.min(selectionRect.startY, selectionRect.endY)}px`,
        width: `${Math.abs(selectionRect.endX - selectionRect.startX)}px`,
        height: `${Math.abs(selectionRect.endY - selectionRect.startY)}px`,
        backgroundColor: "rgba(59, 130, 246, 0.2)",
        border: "2px solid rgba(59, 130, 246, 0.5)",
        borderRadius: "4px",
        pointerEvents: "none" as const,
        zIndex: 10,
      }
    : {};

  return (
    <div className="p-6 bg-white dark:bg-gray-900 min-h-screen transition-colors duration-200">
      {/* Search and Back Button */}
      <div className="flex items-center gap-4 mb-8">
        <button
          onClick={handleBack}
          className="bg-blue-500 hover:bg-blue-600 text-white dark:bg-blue-600 dark:hover:bg-blue-700 px-4 py-2 rounded-md flex items-center transition-colors duration-200"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back
        </button>

        <div className="relative flex-1">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
          </div>
          <input
            type="text"
            className="block w-full max-w-150 pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md leading-5 bg-white dark:bg-gray-800 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:focus:ring-blue-400 dark:focus:border-blue-400 sm:text-sm transition-colors duration-200"
            placeholder="Search project by name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="flex gap-2">
          {/* <button className="px-3 py-2 rounded-md flex items-center font-bold bg-blue-500 text-white border border-b-blue-600 border-b-3">
            <PlusIcon className="h-5 w-5 mr-1.5" />
            New Folder
          </button> */}
          <button
            className="bg-gradient-to-b from-purple-700 to-purple-500 hover:cursor-pointer border-2 border-purple-800 border-b-2 dark:from-purple-600 dark:to-purple-700 dark:hover:from-purple-700 dark:hover:to-purple-800 text-white font-semibold px-4 py-2.5 rounded-lg flex items-center transition-all duration-200 shadow-md"
            onClick={handleCreateNewProject}
          >
            <PlusIcon className="h-5 w-5 mr-1.5" />
            New Video
          </button>
        </div>
      </div>

      {/* Project Stats */}
      {!isLoading && !error && filteredProjects.length > 0 && (
        <div className="mb-4 text-sm text-gray-500 dark:text-gray-400">
          {searchQuery ? (
            <p>
              Found {filteredProjects.length} project
              {filteredProjects.length !== 1 ? "s" : ""}
              {selectedProjects.length > 0
                ? ` • ${selectedProjects.length} selected`
                : ""}
            </p>
          ) : (
            <p>
              {filteredProjects.length} project
              {filteredProjects.length !== 1 ? "s" : ""} total
              {selectedProjects.length > 0
                ? ` • ${selectedProjects.length} selected`
                : ""}
            </p>
          )}
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="text-center py-10">
          <p className="text-gray-500 dark:text-gray-400">
            Loading projects...
          </p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="text-center py-10">
          <p className="text-red-500 dark:text-red-400">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 bg-blue-500 hover:bg-blue-600 text-white dark:bg-blue-600 dark:hover:bg-blue-700 px-4 py-2 rounded-md transition-colors duration-200"
          >
            Retry
          </button>
        </div>
      )}

      {/* Projects Grid */}
      {!isLoading && !error && (
        <div
          ref={containerRef}
          style={{
            minHeight: filteredProjects.length <= 4 ? "" : "auto",
          }}
          className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 relative select-none cursor-default"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          {/* Selection Rectangle */}
          {isDragging && selectionRect && (
            <div style={selectionRectStyle}></div>
          )}

          {/* New Project Card */}
          <div className="flex flex-col cursor-pointer select-none group">
            {/* Card Container */}
            <div
              className="bg-purple-50 dark:bg-purple-900/20 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 ease-in-out relative hover:scale-[1.02] h-40 new-project-card"
              onClick={handleCreateNewProject}
            >
              <div className="w-full h-full flex flex-col items-center justify-center">
                <div className="w-16 h-16 bg-purple-500 dark:bg-purple-600 rounded-lg flex items-center justify-center">
                  <PlusIcon className="h-8 w-8 text-white" />
                </div>
              </div>
            </div>

            {/* Text Container - Separate from card */}
            <div
              className="mt-4 px-1 text-center"
              onClick={handleCreateNewProject}
            >
              <h3 className="font-medium text-purple-600 dark:text-purple-400 select-none">
                New Video
              </h3>
            </div>
          </div>

          {/* Existing Projects */}
          {filteredProjects.map((project) => (
            <div
              key={project.id}
              data-project-id={project.id}
              className="flex flex-col cursor-pointer select-none group"
              onClick={(e) => handleProjectClick(project, e)}
              onContextMenu={(e) => handleContextMenu(e, project.id)}
            >
              {/* Card Container - Only for the image */}
              <div
                className={`bg-white dark:bg-gray-800 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 ease-in-out relative
                  ${
                    selectedProjects.includes(project.id)
                      ? "ring-2 ring-blue-500 dark:ring-blue-400 shadow-md scale-[1.02]"
                      : "hover:scale-[1.02]"
                  }`}
              >
                {/* Selection Indicator */}
                <div
                  className={`absolute top-2 right-2 z-10 w-6 h-6 rounded-full flex items-center justify-center transition-all duration-300 ${
                    selectedProjects.includes(project.id)
                      ? "bg-blue-500 dark:bg-blue-600 opacity-100 scale-100"
                      : "bg-white dark:bg-gray-700 opacity-0 scale-75 group-hover:opacity-100 group-hover:scale-90"
                  }`}
                >
                  {selectedProjects.includes(project.id) ? (
                    <CheckIcon className="h-4 w-4 text-white" />
                  ) : (
                    <div className="w-3 h-3 rounded-sm border-2 border-gray-400 dark:border-gray-500" />
                  )}
                </div>

                {/* Selection Ripple Effect - appears briefly when selecting */}
                {selectedProjects.includes(project.id) && (
                  <div
                    className="absolute inset-0 bg-blue-500/10 dark:bg-blue-600/20 animate-ripple rounded-lg pointer-events-none"
                    style={{ animationDuration: "0.8s" }}
                  />
                )}

                {project.thumbnail ? (
                  <div className="w-full h-40 bg-gray-100 dark:bg-gray-800 relative flex items-center justify-center p-2 overflow-hidden">
                    <img
                      src={project.thumbnail}
                      alt={project.name}
                      className="max-w-full max-h-full object-contain select-none"
                      draggable="false"
                      onError={(e) => {
                        console.error(
                          "Error loading thumbnail for project:",
                          project.id
                        );
                        e.currentTarget.src =
                          "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23cccccc'%3E%3Cpath d='M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-1 16H6c-.55 0-1-.45-1-1V6c0-.55.45-1 1-1h12c.55 0 1 .45 1 1v12c0 .55-.45 1-1 1z'/%3E%3C/svg%3E";
                      }}
                    />
                  </div>
                ) : (
                  <div className="w-full h-40 bg-gray-200 dark:bg-gray-700 flex items-center justify-center relative">
                    <span className="text-gray-500 dark:text-gray-400 select-none">
                      No thumbnail
                    </span>
                  </div>
                )}
              </div>

              {/* Text Container - Separate from card */}
              <div className="mt-2 px-1">
                <h3 className="font-medium text-gray-900 dark:text-white select-none truncate">
                  {project.name}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 select-none mt-0.5">
                  {project.createdAt.toLocaleDateString()}
                </p>
              </div>
            </div>
          ))}

          {/* No Projects Message */}
          {filteredProjects.length === 0 &&
            !isLoading &&
            searchQuery === "" && (
              <div className="col-span-full flex items-center justify-center h-[50vh]">
                <p className="text-gray-500 dark:text-gray-400">
                  No projects yet. Create your first project!
                </p>
              </div>
            )}

          {/* No Search Results */}
          {filteredProjects.length === 0 && searchQuery !== "" && (
            <div className="col-span-full flex items-center justify-center h-[50vh]">
              <p className="text-gray-500 dark:text-gray-400">
                No projects match your search.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Context Menu */}
      {contextMenuPosition && (
        <div
          className="fixed bg-white dark:bg-gray-800 shadow-lg rounded-md py-1 border border-gray-200 dark:border-gray-700 z-50"
          style={{
            left: contextMenuPosition.x,
            top: contextMenuPosition.y,
            minWidth: "180px",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 text-red-600 dark:text-red-400 transition-colors duration-200"
            onClick={() =>
              contextMenuProjectId &&
              handleDeleteProjects(
                selectedProjects.length > 1
                  ? selectedProjects
                  : [contextMenuProjectId]
              )
            }
            disabled={isDeleting}
          >
            <TrashIcon className="h-4 w-4" />
            {selectedProjects.length > 1
              ? `Delete ${selectedProjects.length} projects`
              : "Delete project"}
          </button>
        </div>
      )}

      {/* New Project Dialog */}
      <NewProjectDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onCreateProject={handleProjectCreated}
      />
    </div>
  );
}
